import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useServer } from "@/lib/server-context";
import { useChannels } from "@/lib/feature-hooks";
import { toast } from "sonner";
import { Plus, MessageSquare, Send } from "lucide-react";
import EmptyServer from "@/components/dashboard/EmptyServer";

export default function EmbedMessagesPanel() {
  const { active } = useServer();
  const channels = useChannels(active?.id, active?.bot_present);
  const [list, setList] = useState([]);
  const [form, setForm] = useState({ channel_id: "", title: "", description: "", color: "#FF6A1A", image_url: "", footer: "" });

  const refresh = () => {
    if (!active?.id || !active.bot_present) return;
    api.get(`/features/embed/${active.id}`).then((r) => setList(r.data));
  };
  useEffect(refresh, [active?.id, active?.bot_present]);

  if (!active) return <EmptyServer />;
  if (!active.bot_present) return <EmptyServer needsBot />;

  const send = async (e) => {
    e.preventDefault();
    if (!form.channel_id || !form.title || !form.description) return;
    try {
      await api.post(`/features/embed/${active.id}/send`, form);
      toast.success("Embed posted to channel");
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div data-testid="embed-panel" className="space-y-8">
      <div>
        <div className="overline mb-2"><MessageSquare className="inline w-4 h-4 mr-2 -mt-1" />UTILITIES</div>
        <h1 className="font-heading text-4xl font-bold uppercase tracking-tight">Embed Messages</h1>
        <p className="text-zinc-400 mt-2">Send rich embed announcements to any channel.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <form onSubmit={send} className="tactical-card p-6 space-y-4" data-testid="embed-form">
          <div>
            <label className="overline block mb-2">Channel</label>
            <select className="tactical-input" value={form.channel_id} onChange={(e) => setForm({ ...form, channel_id: e.target.value })} data-testid="embed-channel">
              <option value="">— Select —</option>
              {channels.filter((c) => c.type === "text").map((c) => <option key={c.id} value={c.id}>#{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className="overline block mb-2">Title</label>
            <input className="tactical-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="embed-title" />
          </div>
          <div>
            <label className="overline block mb-2">Description</label>
            <textarea className="tactical-input min-h-[100px]" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="embed-desc" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="overline block mb-2">Color</label>
              <input type="color" className="tactical-input h-10 w-full p-1" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} data-testid="embed-color" />
            </div>
            <div>
              <label className="overline block mb-2">Image URL</label>
              <input className="tactical-input" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} />
            </div>
          </div>
          <div>
            <label className="overline block mb-2">Footer</label>
            <input className="tactical-input" value={form.footer} onChange={(e) => setForm({ ...form, footer: e.target.value })} />
          </div>
          <button type="submit" className="btn-primary w-full justify-center" data-testid="embed-send-btn"><Send className="w-4 h-4" /> Send</button>
        </form>

        <div className="tactical-card p-6">
          <h2 className="font-heading text-lg uppercase mb-4">Live Preview</h2>
          <div className="border-l-4 p-4 rounded-r-lg bg-[#0d1117]" style={{ borderColor: form.color }}>
            <div className="font-bold text-white text-lg">{form.title || "(Title)"}</div>
            <div className="text-zinc-300 text-sm whitespace-pre-wrap mt-1">{form.description || "(Description)"}</div>
            {form.image_url && <img src={form.image_url} alt="" className="mt-3 max-w-full rounded" />}
            {form.footer && <div className="text-zinc-500 text-xs mt-3">{form.footer}</div>}
          </div>

          <div className="mt-6">
            <div className="overline mb-2">Recent</div>
            <div className="space-y-1 max-h-48 overflow-y-auto tactical-scroll">
              {list.slice(0, 10).map((m) => (
                <div key={m.id} className="text-xs text-zinc-400 truncate">{m.title} · <span className="text-zinc-600">{new Date(m.created_at).toLocaleTimeString()}</span></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
